import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { systemPrompts } from '@/api/systemPrompts';
import type {
  SystemPromptRow,
  SystemPromptTranslationRow,
  SystemPromptOverrideRow,
} from '@/api/systemPrompts';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/auth-store';
import { PromptTranslationEditor } from './PromptTranslationEditor';
import { PromptOverrideEditor } from './PromptOverrideEditor';
import { PromptVersionHistory } from './PromptVersionHistory';

interface Props {
  prompt: SystemPromptRow;
}

export function PromptDetailPanel({ prompt }: Props) {
  const user = useAuth((s) => s.user);
  const tenant = useAuth((s) => s.tenant);

  const { data, isLoading } = useQuery({
    queryKey: ['systemPrompt', prompt.id],
    queryFn: () => systemPrompts.get(prompt.id),
  });

  const translations = useMemo<SystemPromptTranslationRow[]>(
    () => data?.translations ?? [],
    [data],
  );
  const overrides = useMemo<SystemPromptOverrideRow[]>(
    () => data?.overrides ?? [],
    [data],
  );

  const isSuperAdmin = Boolean(user?.is_super_admin || tenant?.isSuperAdmin);
  const canWriteOverride =
    user?.role === 'super_admin' ||
    user?.role === 'reseller_owner' ||
    user?.role === 'office_manager';

  // What scope will the override land at? Used to label the override editor.
  const overrideScopeLabel = isSuperAdmin
    ? 'Node-level (super admin, requires ivr_node_id)'
    : tenant?.customerId
      ? 'Customer'
      : tenant?.resellerId
        ? 'Reseller'
        : '';

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 px-4 text-sm text-slate-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading…
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="bg-slate-50/80 border-t border-slate-100 px-4 py-3">
      {data.prompt.description && (
        <p className="text-xs text-slate-500 mb-3">{data.prompt.description}</p>
      )}

      <Tabs defaultValue={isSuperAdmin ? 'translations' : 'overrides'} className="w-full">
        <TabsList className="h-8 p-0.5 bg-slate-100">
          {isSuperAdmin && (
            <TabsTrigger value="translations" className="text-xs h-7">
              Global defaults
            </TabsTrigger>
          )}
          {canWriteOverride && !isSuperAdmin && (
            <TabsTrigger value="overrides" className="text-xs h-7">
              {overrideScopeLabel} override
            </TabsTrigger>
          )}
          {isSuperAdmin && (
            <TabsTrigger value="overrides" className="text-xs h-7">
              Overrides ({overrides.length})
            </TabsTrigger>
          )}
          <TabsTrigger value="versions" className="text-xs h-7">
            History
          </TabsTrigger>
        </TabsList>

        {isSuperAdmin && (
          <TabsContent value="translations" className="mt-3">
            <PromptTranslationEditor
              prompt={data.prompt}
              translations={translations}
            />
          </TabsContent>
        )}

        <TabsContent value="overrides" className="mt-3">
          {canWriteOverride ? (
            <PromptOverrideEditor
              prompt={data.prompt}
              overrides={overrides}
              translations={translations}
              isSuperAdmin={isSuperAdmin}
            />
          ) : (
            <p className="text-xs text-slate-500">
              You don't have permission to write overrides.
            </p>
          )}
        </TabsContent>

        <TabsContent value="versions" className="mt-3">
          <PromptVersionHistory prompt={data.prompt} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
